import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

interface ConfigDialogShellProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  saveLabel: string;
  /** The stale-edit banner, when a save was refused. */
  banner?: ReactNode;
  error?: string | null;
  /** Their value and "Use …'s" for the field this dialog edits, once a conflict is open. */
  note?: ReactNode;
  children: ReactNode;
}

/** The frame the three Dashboard tile-configuration dialogs share. */
export function ConfigDialogShell({
  open,
  title,
  onClose,
  onSave,
  saving,
  saveLabel,
  banner,
  error,
  note,
  children,
}: ConfigDialogShellProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {banner && <Box sx={{ mb: 2 }}>{banner}</Box>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {children}
        {note && (
          <Typography component="div" variant="caption" sx={{ mt: 1.5, color: 'text.secondary' }}>
            {note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
